import React, { useMemo, useState } from "react";
import {
  View,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  ImageBackground,
  Image,
  Platform,
  useWindowDimensions,
  KeyboardAvoidingView,
  ScrollView,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  facebookLoginRequest,
  googleLoginRequest,
  loginRequest,
} from "../../store/auth/authActions";
import { i18nLoadRequest } from "../../store/i18n/i18nActions";
import {
  GoogleBtn,
  Login,
  LoginBckgd,
  FacebookLogin,
  LanguageCaret,
  LanguageCaretUp,
} from "../../assets";
import FacebookButton from "../../assets/login/FacebookButton.png";
import GoogleButton from "../../assets/login/GoogleButton.png";
import LoginLogoNative from "./Animations/LoginLogoNative";
import { useTranslation } from "react-i18next";
import LS from "../../utils/localStorageUtils";
import i18n from "../../i18n/i18n";
import EnFlag from "../../assets/buttonsByLanguage/flags/en_flag.png";
import BgFlag from "../../assets/buttonsByLanguage/flags/bg_flag.png";
import FrFlag from "../../assets/buttonsByLanguage/flags/fr_flag.png";
import RuFlag from "../../assets/buttonsByLanguage/flags/ru_flag.png";
import TrFlag from "../../assets/buttonsByLanguage/flags/tr_flag.png";
import { LanguagePicker } from "./common/LanguagePicker";

const languages = [
  { code: "en", id: 1, label: "English" },
  { code: "bg", id: 2, label: "Български" },
  { code: "fr", id: 3, label: "Français" },
  { code: "ru", id: 4, label: "Русский" },
  { code: "tr", id: 5, label: "Türkçe" },
];

const languageFlags: Record<string, any> = {
  en: EnFlag,
  bg: BgFlag,
  fr: FrFlag,
  ru: RuFlag,
  tr: TrFlag,
};

type LoginIdentity = "username" | "phone";

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const LoginPage: React.FC = () => {
  const [identity] = useState<LoginIdentity>("username");
  const [data, setData] = useState({
    username: "",
    mobileNumber: "",
    password: "",
  });
  const [errors, setErrors] = useState<{
    username?: string;
    mobileNumber?: string;
    password?: string;
  }>({});
  const [isFocused, setIsFocused] = useState(false);
  const [isFocusedPass, setIsFocusedPass] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const authLoading = useSelector((s: RootState) => s.auth.loading);
  const authError = useSelector((s: RootState) => s.auth.error);
  const langCode = useSelector((s: RootState) => s.i18n?.langCode ?? "en");

  const { width, height } = useWindowDimensions();
  const isShort = height < 700;

  const formWidth = useMemo(() => clamp(width * 0.86, 280, 420), [width]);
  const inputWidth = useMemo(() => clamp(width * 0.7, 260, 420), [width]);

  const headerHeight = useMemo(() => clamp(height * 0.45, 300, 420), [height]);

  const topPad = useMemo(() => clamp(height * 0.06, 16, 44), [height]);

  const onChange = (name: keyof typeof data, value: string) =>
    setData((d) => ({ ...d, [name]: value }));

  const submitData = () => {
    const payload = {
      username: data.username?.trim(),
      password: data.password || "",
    };

    if (!payload.username || !payload.password) {
      setErrors({
        username: !payload.username ? "Username is required" : undefined,
        password: !payload.password ? "Password is required" : undefined,
      });
      return;
    }

    setErrors({});
    dispatch(loginRequest(payload));
  };

  const handleChangeLanguage = (code: string) => {
    i18n.changeLanguage(code).catch(() => {});
    LS.set("lang", code);

    const lang = languages.find((l) => l.code === code);
    if (lang) {
      dispatch(
        i18nLoadRequest({
          langId: lang.id,
          langCode: lang.code,
        }),
      );
    }
  };

  const label = t("T_SITE_LOGIN_LOGIN_G");
  const parts = label.split(/<br\s*\/?>/i);
  const labelFb = t("T_SITE_LOGIN_LOGIN_FB");
  const partsFb = labelFb.split(/<br\s*\/?>/i);

  const handleGoogleLogin = () => dispatch(googleLoginRequest());
  const handleFacebookLogin = () => dispatch(facebookLoginRequest());

  const webNoOutline =
    Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null;

  return (
    <View style={styles.overlayRoot}>
      <View style={styles.overlayBackdrop} />

      <View style={styles.modalContainer}>
        <ImageBackground
          source={LoginBckgd}
          resizeMode="stretch"
          style={styles.backgroundImage}
        >
          <KeyboardAvoidingView
            style={styles.kav}
            behavior={Platform.OS === "ios" ? "padding" : undefined}
          >
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={[
                styles.scrollContent,
                { paddingBottom: 24 },
              ]}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              {/* LOGO */}
              <View
                style={[
                  styles.header,
                  {
                    height: headerHeight,
                    justifyContent: "flex-end",
                    paddingBottom: 9,
                    marginTop: 16,
                  },
                ]}
              >
                <LoginLogoNative />
              </View>

              {/* FORM */}
              <View style={[styles.content, { width: formWidth }]}>
                {/* Username */}
                <View style={[styles.field, { marginTop: 10 }]}>
                  <Text style={styles.label}>{t("T_SITE_LOGIN_USERNAME")}</Text>
                  <TextInput
                    value={data.username}
                    onChangeText={(v) => onChange("username", v)}
                    placeholder={isFocused ? "" : "Username"}
                    placeholderTextColor="#94A3B8"
                    style={[
                      styles.input,
                      !!errors.username && styles.inputError,
                      webNoOutline,
                      { width: inputWidth },
                    ]}
                    autoCapitalize="none"
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    returnKeyType="next"
                  />
                  <Text style={styles.helperText}>
                    {errors.username ?? "\u00a0"}
                  </Text>
                </View>

                {/* Password */}
                <View style={styles.field}>
                  <Text style={styles.label}>{t("T_SITE_LOGIN_PASSWORD")}</Text>
                  <TextInput
                    value={data.password}
                    onChangeText={(v) => onChange("password", v)}
                    placeholder={isFocusedPass ? "" : "●●●●●●●●●●●"}
                    placeholderTextColor="#94A3B8"
                    onFocus={() => setIsFocusedPass(true)}
                    onBlur={() => setIsFocusedPass(false)}
                    secureTextEntry
                    style={[
                      styles.input,
                      !!errors.password && styles.inputError,
                      webNoOutline,
                      { width: inputWidth },
                    ]}
                    autoCapitalize="none"
                    onSubmitEditing={submitData}
                    returnKeyType="done"
                  />

                  <Text style={styles.helperText}>
                    {errors.password ?? "\u00a0"}
                  </Text>

                  {authError ? (
                    <Text style={styles.errorText}>{authError}</Text>
                  ) : null}
                </View>

                {/* <View style={[styles.optionsRow, { width: inputWidth }]}>
                  <TouchableOpacity style={styles.rememberRow}>
                    <View style={styles.checkbox} />
                    <Text style={styles.rememberText}>Remember me</Text>
                  </TouchableOpacity>

                  <TouchableOpacity>
                    <Text style={styles.forgotText}>
                      {t("T_SITE_LOST_PASSWORD_TITLE")}
                    </Text>
                  </TouchableOpacity>
                </View> */}

                {/* Buttons */}
                <View
                  style={{ marginTop: isShort ? 0 : 2, alignItems: "center" }}
                >
                  <TouchableOpacity
                    onPress={submitData}
                    style={authLoading ? { opacity: 0.7 } : undefined}
                  >
                    <ImageBackground
                      source={Login}
                      resizeMode="contain"
                      style={styles.loginBtn}
                    >
                      <Text
                        style={[
                          styles.login,
                          (langCode === "fr" || langCode === "fr-FR") && {
                            fontSize: 10,
                          },
                        ]}
                      >
                        {t("T_SITE_LOGIN_TITLE")}
                      </Text>
                    </ImageBackground>
                  </TouchableOpacity>
                  <View style={{ flexDirection: "row" }}>
                    <TouchableOpacity onPress={handleFacebookLogin}>
                      <Image
                        source={FacebookButton}
                        style={styles.socialButton}
                        resizeMode="stretch"
                      />
                    </TouchableOpacity>

                    <TouchableOpacity onPress={handleGoogleLogin}>
                      <Image
                        source={GoogleButton}
                        style={styles.socialButton}
                        resizeMode="stretch"
                      />
                    </TouchableOpacity>
                  </View>

                  <View
                    style={{
                      marginTop: 12,
                      width: inputWidth,
                      alignItems: "center",
                    }}
                  >
                    <LanguagePicker
                      value={langCode}
                      options={languages.map((l) => ({
                        code: l.code,
                        label: l.label,
                        flag: languageFlags[l.code],
                      }))}
                      caretIcon={LanguageCaret}
                      caretIconOpen={LanguageCaretUp}
                      buttonStyle={{
                        backgroundColor: "#4A2530",
                        height: 40,
                        width: inputWidth,
                        borderColor: "#ab8b68",
                        borderWidth: 1,
                      }}
                      menuStyle={{
                        backgroundColor: "#211015",
                        borderColor: "#ab8b68",
                        borderWidth: 1,
                        marginBottom: 2,
                        width: inputWidth,
                      }}
                      menuItemActiveStyle={{
                        backgroundColor: "#4A2530",
                        borderRadius: 4,

                        border: "1px solid #ab8b68",
                      }}
                      onChange={(code) => {
                        const lang = languages.find((l) => l.code === code);
                        if (lang)
                          dispatch(
                            i18nLoadRequest({
                              langId: lang.id,
                              langCode: lang.code,
                            }),
                          );
                      }}
                    />
                  </View>

                  {/* <View
                    style={[
                      styles.field,
                      { marginTop: isShort ? 0 : 2, width: 160 },
                    ]}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        // backgroundColor: "#4A2530",
                        borderRadius: 8,
                        paddingHorizontal: 8,
                        paddingVertical: 6,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {languages.map((l) => {
                        const flagSrc = languageFlags[l.code];
                        if (!flagSrc) return null;
                        const isActive = langCode === l.code;

                        return (
                          <TouchableOpacity
                            key={l.code}
                            onPress={() => handleChangeLanguage(l.code)}
                            style={{
                              paddingHorizontal: 4,
                              paddingVertical: 2,
                              borderRadius: 6,
                              opacity: isActive ? 1 : 0.8,
                              // borderWidth: isActive ? 1 : 0,
                              // borderColor: isActive ? "#FFEAA0" : "transparent",
                            }}
                          >
                            <Image
                              source={flagSrc}
                              style={{ width: 42, height: 38 }}
                              resizeMode="contain"
                            />
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View> */}
                </View>
              </View>
            </ScrollView>
          </KeyboardAvoidingView>
        </ImageBackground>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlayRoot: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  overlayBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.8)",
  },

  // важно: да има височина, иначе на някои устройства ScrollView “няма място”
  modalContainer: {
    flex: 1,
    width: "100%",
    maxHeight: "100%",
    borderRadius: 18,
    padding: 2,
  },
  login: {
    color: "#FFffff",
    fontWeight: "bold",
    fontSize: 15,

    textTransform: "uppercase",
  },
  social: {
    color: "#FFffff",
    fontWeight: "bold",
    fontSize: 12,
    marginLeft: 26,

    textTransform: "uppercase",
  },

  backgroundImage: {
    flex: 1,
    width: "100%",
    height: "100%",
    alignItems: "center",
  },

  kav: {
    flex: 1,
    width: "100%",
  },

  scroll: {
    flex: 1,
    width: "100%",
  },

  scrollContent: {
    flexGrow: 1,
    alignItems: "center",
  },

  header: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: "transparent",
  },

  content: {
    paddingHorizontal: 16,
    paddingTop: 16,
    alignItems: "center",
  },

  field: {
    marginBottom: 0,
  },

  label: {
    color: "#C6A6AE",
    fontSize: 12,
    letterSpacing: 1,
    marginBottom: 4,
    textTransform: "uppercase",
    alignSelf: "flex-start",
  },

  input: {
    height: 40,
    backgroundColor: "#4A2530",
    borderRadius: 8,
    paddingHorizontal: 14,
    color: "#fff",
    fontSize: 16,
    includeFontPadding: false,
    textAlignVertical: "center",
  },

  inputError: {
    borderColor: "#ff6b6b",
    borderWidth: 1,
  },

  helperText: {
    color: "#94A3B8",
    marginTop: 0,
    fontSize: 12,
    minHeight: 16,
    alignSelf: "flex-start",
  },

  errorText: {
    color: "#ff6b6b",
    marginTop: 4,
    fontSize: 12,
    minHeight: 16,
    alignSelf: "flex-start",
  },

  optionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: -2,
  },

  rememberRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#C6A6AE",
    marginRight: 8,
    justifyContent: "center",
    alignItems: "center",
  },

  rememberText: {
    color: "#C6A6AE",
    fontSize: 14,
  },

  forgotText: {
    color: "#E63950",
    fontSize: 14,
  },

  loginBtn: {
    width: 200,
    height: 50,
    alignItems: "center",
    justifyContent: "center",
  },

  socialButton: {
    // width: 142,
    width: 56,
    height: 52,
    marginTop: 6,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default LoginPage;
